/**
 * «Show me that card» — a scroll, and nothing else.
 *
 * A pin on the map carries `ref`, the id of the board element it stands for
 * (`Pin.ref`). Tapping it brings that card into view: the guide has ALREADY put
 * the object on the board and described it, so asking the model about it again
 * would spend a run to be told what is on screen. This is the cheapest correct
 * answer — no run, no message, no state.
 *
 * **It reads the DOM rather than the store on purpose.** `BoardItem` stamps
 * `data-id` on every card it draws, and what this needs to know is not «is this
 * component in the surface» but «is it on the screen right now»: below the
 * two-column breakpoint the board may be on the other tab, and in the
 * navigation mode it is hidden behind the map. In both cases the element is
 * absent and the tap correctly does nothing, which a store lookup would have
 * had to special-case twice.
 */

import { prefersReducedMotion } from '../lib/reducedMotion';

/** How long the card is outlined after the scroll. Long enough to find it. */
const PULSE_MS = 1200;

/** Brings a board card into view. Returns whether one was found. */
export function focusBoardCard(id: string): boolean {
  if (typeof document === 'undefined') return false;
  // `[data-id]` is `BoardItem`'s own attribute; the selector is escaped because
  // a component id carries a colon («obj:40»), which is CSS syntax.
  const node = document.querySelector<HTMLElement>(`[data-id="${cssEscape(id)}"]`);
  if (!node) return false;

  node.scrollIntoView({
    block: 'nearest',
    behavior: prefersReducedMotion() ? 'auto' : 'smooth',
  });

  // The outline is animated through the Web Animations API rather than by
  // toggling a class: `BoardItem` computes its own `className` from the store
  // and would wipe one on its next render, which happens on every frame of a
  // turn. An animation is attached to the element, not to its attributes.
  if (!prefersReducedMotion() && typeof node.animate === 'function') {
    const accent = getComputedStyle(document.documentElement)
      .getPropertyValue('--accent')
      .trim();
    node.animate(
      [
        { boxShadow: `0 0 0 0 ${accent || '#172651'}` },
        { boxShadow: `0 0 0 3px ${accent || '#172651'}` },
        { boxShadow: '0 0 0 0 rgba(0,0,0,0)' },
      ],
      { duration: PULSE_MS, easing: 'ease-out' },
    );
  }
  return true;
}

/** `CSS.escape` where it exists; a conservative fallback where it does not. */
function cssEscape(value: string): string {
  const api = (globalThis as { CSS?: { escape?: (v: string) => string } }).CSS;
  if (typeof api?.escape === 'function') return api.escape(value);
  return value.replace(/["\\]/g, '\\$&');
}
